import { Experiment, Result } from "@growthbook/growthbook-react";
import config, { SCANNER_FLAG } from "config/config";
import { ScannerAnalyticsEventType } from "features/shared/hooks/analytics/useScannerAnalytics";
import { MultiPaxSessionId } from "models/session/session";

import { UAParser } from "ua-parser-js";

export type AnalyticsEventType =
  // general
  | "app_loaded"
  | "app_error"
  | "page_view"
  // login
  | "login_form_validation_failed"
  | "login_booking_not_found"
  | "login_booking_ineligible"
  | "login_captcha_failed"
  | "login_success"
  // consent
  | "consent_revoked"
  // questions
  | "questions_presented"
  | "questions_answered"
  // scan
  | ScannerAnalyticsEventType
  // upload
  | "upload_file_selected"
  | "upload_selected"
  // manual
  | "manual_selected"
  | "form_touched"
  | "form_validation_error"
  | "form_mismatch"
  // apply
  | "apply_started"
  | "apply_ineligible"
  | "apply_failed"
  | "apply_recognition_failed"
  | "apply_success"
  // ready to fly
  | "pax_ready_to_fly"
  | "all_paxes_ready_to_fly"
  | "pax_became_unresolved"
  // a/b
  | "experiment_viewed";

interface DeferredEvent {
  eventType: AnalyticsEventType;
  params: { [key: string]: any };
}

const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
] as const;

type UtmKey = (typeof UTM_KEYS)[number];

// Umami's tracker script can load after the first analytics events fire. We
// poll for it and flush deferred events once it's ready, giving up after
// UMAMI_MAX_POLL_ATTEMPTS * UMAMI_POLL_INTERVAL_MS (5s).
const UMAMI_POLL_INTERVAL_MS = 100;
const UMAMI_MAX_POLL_ATTEMPTS = 50;

class AnalyticsService {
  private static instance: AnalyticsService | undefined;

  // Lazily-instantiated singleton. Using a getter (rather than assigning only
  // inside init()) guarantees `shared` is never undefined — even when a child
  // component's effect fires before App's init() effect runs. React runs child
  // effects before parent effects, so landing directly on a top-level route
  // like /ineligible previously crashed with "Cannot read properties of
  // undefined (reading 'trackEvent')".
  static get shared(): AnalyticsService {
    if (!AnalyticsService.instance) {
      AnalyticsService.instance = new AnalyticsService();
    }
    return AnalyticsService.instance;
  }

  // Note: no init() method — the getter above constructs the singleton on first
  // access, which is the earliest any code touches it. An explicit init() would
  // be redundant.

  private sessionId: MultiPaxSessionId | undefined;
  private paxSessionId: string | undefined;
  private scanId: string | undefined;

  private consent: boolean | undefined;

  private deferredUmamiEvents: DeferredEvent[];

  private localEventCounter: number;

  private umamiWaitActive: boolean;

  private utmParams: Partial<Record<UtmKey, string>>;

  setSessionId(sessionId: MultiPaxSessionId) {
    this.sessionId = sessionId;
  }

  setPaxSessionId(paxSessionId: string) {
    this.paxSessionId = paxSessionId;
  }

  setScanId(scanId: string) {
    this.scanId = scanId;
  }

  private isUmamiReady(): boolean {
    return typeof window.umami?.track === "function";
  }

  private startUmamiPoll() {
    if (this.umamiWaitActive) {
      return;
    }

    this.umamiWaitActive = true;

    let attempts = 0;
    const intervalId = window.setInterval(() => {
      attempts += 1;

      if (this.isUmamiReady()) {
        window.clearInterval(intervalId);
        this.umamiWaitActive = false;
        this.processDeferredUmamiEvents();
        return;
      }

      if (attempts >= UMAMI_MAX_POLL_ATTEMPTS) {
        window.clearInterval(intervalId);
        this.umamiWaitActive = false;
        console.warn(
          `[analytics] Umami tracker did not load within ${
            (UMAMI_MAX_POLL_ATTEMPTS * UMAMI_POLL_INTERVAL_MS) / 1000
          }s; ${this.deferredUmamiEvents.length} event(s) dropped.`
        );
        this.deferredUmamiEvents = [];
      }
    }, UMAMI_POLL_INTERVAL_MS);
  }

  private processDeferredUmamiEvents() {
    if (this.consent === false) {
      this.deferredUmamiEvents = [];
      return;
    }

    if (this.consent !== true || this.deferredUmamiEvents.length === 0) {
      return;
    }

    if (!this.isUmamiReady()) {
      this.startUmamiPoll();
      return;
    }

    const events = [...this.deferredUmamiEvents];
    this.deferredUmamiEvents = [];
    events.forEach((event) =>
      this.fireUmamiEvent(event.eventType, event.params)
    );
  }

  setConsentStatus(consent: boolean) {
    this.consent = consent;
    this.processDeferredUmamiEvents();
  }

  private fireUmamiEvent(
    eventType: AnalyticsEventType,
    params: { [key: string]: any }
  ) {
    if (!this.isUmamiReady()) {
      this.deferredUmamiEvents.push({ eventType, params });
      this.startUmamiPoll();
      return;
    }

    const websiteId = config.umami.websiteId;
    const isPageView = eventType === "page_view";

    if (websiteId) {
      params["website_id"] = websiteId;
    }

    // Build UTM query string so Umami's server can parse it into its native
    // utm_source/utm_medium/etc. columns (powers the UTM breakdown UI).
    // We re-append only UTM params to the cleaned URL since page_path strips
    // the full query string to avoid leaking PII (session tokens etc.).
    const utmQuery = new URLSearchParams(
      this.utmParams as Record<string, string>
    ).toString();
    const urlWithUtm = utmQuery
      ? `${params.page_path}?${utmQuery}`
      : params.page_path;

    window.umami.track((props: { website?: string }) => {
      const payload: {
        website?: string;
        language?: string;
        url?: string;
        title?: string;
        name?: AnalyticsEventType;
        data: { [key: string]: any };
      } = {
        ...props,
        data: params,
        website: websiteId ?? props.website,
      };

      if (urlWithUtm) {
        payload.url = urlWithUtm;
      }
      if (params.language) {
        payload.language = params.language;
      }
      if (params.page_title) {
        payload.title = params.page_title;
      }
      if (!isPageView) {
        payload.name = eventType;
      }

      return payload;
    });
  }

  private enrichParamsFromUa(params: { [key: string]: any }) {
    const uaResult = UAParser();
    params["ua"] = uaResult.ua;
    if (uaResult.browser.name) {
      params["browser_name"] = uaResult.browser.name;
    }
    if (uaResult.browser.version) {
      params["browser_version"] = uaResult.browser.version;
    }
    if (uaResult.os.name) {
      params["os_name"] = uaResult.os.name;
    }
    if (uaResult.os.version) {
      params["os_version"] = uaResult.os.version;
    }
    if (uaResult.device.model) {
      params["device_model"] = uaResult.device.model;
    }
    if (uaResult.device.type) {
      params["device_type"] = uaResult.device.type;
    }
    if (uaResult.device.vendor) {
      params["device_vendor"] = uaResult.device.vendor;
    }
    if (uaResult.engine.name) {
      params["engine_name"] = uaResult.engine.name;
    }
    if (uaResult.engine.version) {
      params["engine_version"] = uaResult.engine.version;
    }
    if (uaResult.cpu.architecture) {
      params["cpu_architecture"] = uaResult.cpu.architecture;
    }
  }

  // track event
  trackEvent(
    eventType: AnalyticsEventType,
    params: { [key: string]: any },
    isScanRelated: boolean
  ) {
    if (this.sessionId !== undefined) {
      params["multipax_session_id"] = this.sessionId;
    }
    if (this.paxSessionId !== undefined) {
      params["pax_session_id"] = this.paxSessionId;
    }
    if (this.scanId !== undefined && isScanRelated) {
      params["scan_id"] = this.scanId;
    }
    params["local_counter"] = this.localEventCounter;

    Object.assign(params, this.utmParams);

    this.enrichParamsFromUa(params);

    if (this.consent === true) {
      this.fireUmamiEvent(eventType, params);
    } else if (this.consent !== false) {
      // add to stack for later
      this.deferredUmamiEvents.push({
        eventType: eventType,
        params: params,
      });
    }

    this.localEventCounter += 1;
  }

  // track experiment (growthbook)
  trackExperiment(experiment: Experiment<any>, result: Result<any>) {
    // The scanner A/B flag drives which scanner a scan uses, so its exposure is
    // a scan-related event (kept with scan analytics for rollout metrics).
    const isScanEvent = experiment.key === SCANNER_FLAG;

    this.trackEvent(
      "experiment_viewed",
      {
        experiment_id: experiment.key,
        variation_id: result.key,
      },
      isScanEvent
    );
  }

  private constructor() {
    this.deferredUmamiEvents = [];
    this.localEventCounter = 0;
    this.umamiWaitActive = false;
    this.utmParams = this.captureUtmParams();
  }

  private captureUtmParams(): Partial<Record<UtmKey, string>> {
    try {
      const search = new URLSearchParams(window.location.search);
      const result: Partial<Record<UtmKey, string>> = {};
      for (const key of UTM_KEYS) {
        const val = search.get(key);
        if (val) result[key] = val;
      }
      return result;
    } catch {
      return {};
    }
  }
}

export default AnalyticsService;
